import { type Condition } from "@/types";

import {
  type ActionReference,
  type ComputeRequirements,
  type ContainerParameters,
  type InvocationDataSource,
  type InvocationInput,
  type InvocationUploadDestination,
} from "./ActionRecord";

export enum TriggerEvaluationCause {
  DatasetMetadataUpdate = "dataset_metadata_update",
  FileUpload = "file_upload",
  FileIngest = "file_ingest",
  FileMetadataUpdate = "file_metadata_update",
  RecurringSchedule = "recurring_schedule",
}

const causeToString: Record<TriggerEvaluationCause, string> = {
  [TriggerEvaluationCause.FileUpload]: "File Uploaded",
  [TriggerEvaluationCause.DatasetMetadataUpdate]: "Dataset Metadata Updated",
  [TriggerEvaluationCause.FileIngest]: "File Ingested",
  [TriggerEvaluationCause.FileMetadataUpdate]: "File Metadata Updated",
  [TriggerEvaluationCause.RecurringSchedule]: "Recurring Schedule",
};

export function causeToFormattedString(cause: TriggerEvaluationCause): string {
  return causeToString[cause];
}

export enum TriggerEvaluationOutcome {
  InvokedAction = "invoked_action",
  Skipped = "skipped",
}

export enum TriggerEvaluationOutcomeReason {
  AlreadyRun = "already_run",
  ConditionNotMet = "condition_not_met",
  NoMatchingFiles = "no_matching_files",
  TriggerDisabled = "trigger_disabled",
}

export enum TriggerEvaluationStatus {
  Pending = "pending",
  Evaluated = "evaluated",
  Failed = "failed",
}

export interface TriggerEvaluationRecord {
  trigger_evaluation_id: number;
  trigger_id: string;
  data_source: InvocationDataSource;
  evaluation_start: string;
  evaluation_end?: string;
  status: TriggerEvaluationStatus;
  status_detail?: string;
  outcome?: TriggerEvaluationOutcome;
  outcome_reason?: TriggerEvaluationOutcomeReason;
  cause?: TriggerEvaluationCause;
}

export interface TriggerEvaluationSummary {
  count_pending: number;
  last_evaluation_start: string | null;
}

export enum TriggerForEachPrimitive {
  Dataset = "dataset",
  DatasetFile = "dataset_file",
}

export enum TriggerType {
  EventDriven = "event_driven",
  Scheduled = "scheduled",
}

/**
 * Trigger properties specific to event-driven triggers.
 */
export interface TriggerOnEvent {
  causes: TriggerEvaluationCause[];
  for_each: TriggerForEachPrimitive;
  required_inputs: string[];
  additional_inputs: string[] | null;
  condition: Condition | null;
}

/**
 * Trigger properties specific to scheduled triggers.
 */
export interface TriggerOnSchedule {
  schedule: string;
  invocation_input: InvocationInput | null;
  next_occurrence: string | null;
}

/**
 * Database record for an event-driven trigger.
 */
export interface TriggerRecord {
  action: ActionReference;
  additional_inputs?: string[];
  causes: TriggerEvaluationCause[];
  compute_requirement_overrides?: ComputeRequirements;
  condition?: Condition;
  container_parameter_overrides?: ContainerParameters;
  created: string;
  created_by: string;
  enabled: boolean;
  for_each: TriggerForEachPrimitive;
  modified: string;
  modified_by: string;
  name: string;
  org_id: string;
  parameter_values: Record<string, unknown>;
  required_inputs: string[];
  service_user_id: string;
  timeout?: number;
  trigger_id: string;
}

/**
 * Database record for a scheduled trigger.
 */
export interface ScheduledTriggerRecord {
  action: ActionReference;
  compute_requirement_overrides: ComputeRequirements | null;
  container_parameter_overrides: ContainerParameters | null;
  created: string;
  created_by: string;
  enabled: boolean;
  invocation_input: InvocationInput | null;
  invocation_upload_destination: InvocationUploadDestination | null;
  modified: string;
  modified_by: string;
  name: string;
  next_occurrence: string | null;
  org_id: string;
  parameter_values: Record<string, unknown> | null;
  schedule: string;
  timeout: number | null;
  trigger_id: string;
}

/**
 * Database view record representing either an event-driven or a scheduled trigger.
 *
 * Depending on `trigger_type`, either `on_event` will be set, or `on_schedule`.
 */
export interface TriggerView {
  action: ActionReference;
  compute_requirement_overrides: ComputeRequirements | null;
  container_parameter_overrides: ContainerParameters | null;
  created: string;
  created_by: string;
  enabled: boolean;
  invocation_upload_destination: InvocationUploadDestination | null;
  modified: string;
  modified_by: string;
  name: string;
  on_event: TriggerOnEvent | null;
  on_schedule: TriggerOnSchedule | null;
  org_id: string;
  parameter_values: Record<string, unknown> | null;
  service_user_id: string;
  timeout: number | null;
  trigger_id: string;
  trigger_type: TriggerType;
}

export class TriggerViewConverter {
  static convertEventTriggerRecord(record: TriggerRecord): TriggerView {
    return {
      trigger_type: TriggerType.EventDriven,
      trigger_id: record.trigger_id,
      name: record.name,
      enabled: record.enabled,
      on_event: {
        causes: record.causes,
        for_each: record.for_each,
        required_inputs: record.required_inputs,
        additional_inputs: record.additional_inputs ?? null,
        condition: record.condition ?? null,
      },
      action: record.action,
      compute_requirement_overrides:
        record.compute_requirement_overrides ?? null,
      container_parameter_overrides:
        record.container_parameter_overrides ?? null,
      parameter_values: record.parameter_values,
      service_user_id: record.service_user_id,
      created: record.created,
      created_by: record.created_by,
      modified: record.modified,
      modified_by: record.modified_by,
      org_id: record.org_id,
      timeout: record.timeout ?? null,
      invocation_upload_destination: null,
      on_schedule: null,
    };
  }

  static convertScheduledTriggerRecord(
    record: ScheduledTriggerRecord,
  ): TriggerView {
    return {
      trigger_type: TriggerType.Scheduled,
      trigger_id: record.trigger_id,
      name: record.name,
      enabled: record.enabled,
      action: record.action,
      on_schedule: {
        schedule: record.schedule,
        invocation_input: record.invocation_input,
        next_occurrence: record.next_occurrence,
      },
      compute_requirement_overrides: record.compute_requirement_overrides,
      container_parameter_overrides: record.container_parameter_overrides,
      parameter_values: record.parameter_values,
      service_user_id: `actions_service_user@${record.org_id}.srv`,
      created: record.created,
      created_by: record.created_by,
      modified: record.modified,
      modified_by: record.modified_by,
      org_id: record.org_id,
      timeout: record.timeout,
      invocation_upload_destination: record.invocation_upload_destination,
      on_event: null,
    };
  }
}

export type UpdateTriggerRequest = Partial<{
  action_name: string;
  action_owner_id: string;
  action_digest: string;
  additional_inputs: string[];
  causes: TriggerEvaluationCause[];
  compute_requirement_overrides: ComputeRequirements;
  container_parameter_overrides: ContainerParameters;
  condition: Condition | null;
  enabled: boolean;
  for_each: TriggerForEachPrimitive;
  parameter_values: Record<string, unknown>;
  required_inputs: string[];
  timeout: number;
}>;

export interface BaseCreateTriggerRequest {
  action_name: string;
  action_owner_id: string | null;
  compute_requirement_overrides: ComputeRequirements | null;
  container_parameter_overrides: ContainerParameters | null;
  enabled: boolean;
  name: string;
  parameter_values: Record<string, unknown> | null;
  timeout: number | null;
}

export interface CreateEventTriggerRequest extends BaseCreateTriggerRequest {
  action_digest: string | null;
  additional_inputs: string[] | null;
  causes: TriggerEvaluationCause[] | null;
  condition: Condition | null;
  for_each: TriggerForEachPrimitive;
  required_inputs: string[];
  service_user_id: string | null;
}

export interface CreateScheduledTriggerRequest
  extends BaseCreateTriggerRequest {
  invocation_input: InvocationInput | null;
  invocation_upload_destination: InvocationUploadDestination | null;
  schedule: string;
}
